import { useTranslation } from "react-i18next";
import React, { useState, useEffect, useRef, useCallback, useLayoutEffect } from "react";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";

// Icons
import { FaCarAlt, FaEdit, FaMapMarkerAlt, FaTrash } from "react-icons/fa";

// Service
import { controlService } from "../../Services/control.service";
import { visitsService } from "../../Services/visits.service";

// Components
import RadiusCoordinatedMap from "../Components/Maps/RadiusCoordinatedMap";
import { ToastComponent } from "../Components/ToastComponent";
import { getLocationCoordinates } from "../../Services/locations.service";

// Libs
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import { Edit, GridComponent, Page, ColumnDirective, ColumnsDirective, Inject } from "@syncfusion/ej2-react-grids";

const MARKER_TYPE_ID = 2; // Pontos de Visita

const VisitSidebar = ({
  markerType,
  setMarkerType,
  markerName,
  setMarkerName,
  radius,
  setRadius,
  address,
  setAddress,
  latitude,
  setLatitude,
  longitude,
  setLongitude,
  handleView,
  handleInsert,
  isViewMode = false,
  isEditing = false,
  handleEditFromView,
  loading = false,
  savingPoint = false,
  setToastParams
}) => {
  const fieldsDisabled = isViewMode && !isEditing;

  const nameInputRef = useRef(null);
  const addressInputRef = useRef(null);
  const latInputRef = useRef(null);
  const lngInputRef = useRef(null);

  const inputValuesRef = useRef({
    name: '',
    address: '',
    lat: '',
    lng: ''
  });

  // No componente PAI (VisitSidebar ou acima dela)
  useEffect(() => {
    if (latInputRef.current && latitude) {
      latInputRef.current.value = latitude;
      inputValuesRef.current.lat = latitude;
    }
  }, [latitude]);

  useEffect(() => {
    if (lngInputRef.current && longitude) {
      lngInputRef.current.value = longitude;
      inputValuesRef.current.lng = longitude;
    }
  }, [longitude]);


  useEffect(() => {
    if (isViewMode || isEditing) {
      if (nameInputRef.current && markerName) {
        nameInputRef.current.value = markerName;
        inputValuesRef.current.name = markerName;
      }
      if (addressInputRef.current && address) {
        addressInputRef.current.value = address;
        inputValuesRef.current.address = address;
      }
      if (latInputRef.current && latitude) {
        latInputRef.current.value = latitude;
        inputValuesRef.current.lat = latitude;
      }
      if (lngInputRef.current && longitude) {
        lngInputRef.current.value = longitude;
        inputValuesRef.current.lng = longitude;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      style={{
        position: "absolute",
        top: 150,
        left: 310,
        zIndex: 9999,
        width: 330,
        padding: "20px 18px",
        borderRadius: 12,
        background: "var(--bg-card)",
        color: "#fff",
        boxShadow: "0 4px 14px rgba(0,0,0,0.45)",
        fontSize: 14
      }}
    >
      <h5 style={{ marginBottom: 12 }}>
        {isViewMode && !isEditing ? "Visualizar marcador" : isEditing ? "Editar marcador" : "Criar marcador por:"}
      </h5>

      <div style={{ display: "flex", gap: 10, marginBottom: 20 }}>
        <button
          className={`btn ${markerType === "end" ? "btn-primary" : "btn-outline-primary"}`}
          style={{ flex: 1 }}
          onClick={() => setMarkerType("end")}
          disabled={fieldsDisabled}
        >
          Endereço
        </button>

        <button
          className={`btn ${markerType === "coord" ? "btn-primary" : "btn-outline-primary"}`}
          style={{ flex: 1 }}
          onClick={() => setMarkerType("coord")}
          disabled={fieldsDisabled}
        >
          Coordenada
        </button>
      </div>

      <label style={{ fontSize: 13, marginBottom: 4 }}>Nome</label>
      <input
        ref={nameInputRef}
        type="text"
        className="form-control mb-3"
        placeholder="Marcador"
        defaultValue={inputValuesRef.current.name}
        onChange={(e) => { inputValuesRef.current.name = e.target.value; }}
        disabled={fieldsDisabled}
      />

      <label style={{ fontSize: 13, marginBottom: 4 }}>Raio (m)</label>
      <select
        className="form-control mb-3"
        value={radius ?? 50}
        onChange={(e) => setRadius(e.target.value)}
        disabled={fieldsDisabled}
      >
        <option value="0">-- Raio --</option>
        <option value="50">50</option>
        <option value="100">100</option>
        <option value="200">200</option>
        <option value="500">500</option>
        <option value="1000">1000</option>
      </select>

      <div style={{ display: markerType === "end" ? "block" : "none" }}>
        <label style={{ fontSize: 13, marginBottom: 4 }}>Endereço</label>
        <input
          ref={(el) => {
            addressInputRef.current = el;
            if (el) {
              if (inputValuesRef.current.address && !el.value) {
                el.value = inputValuesRef.current.address;
              }
            }
          }}
          type="text"
          className="form-control mb-3"
          placeholder="Digite o endereço..."
          defaultValue={inputValuesRef.current.address}
          onChange={(e) => {
            inputValuesRef.current.address = e.target.value;
          }}
          disabled={fieldsDisabled}
        />
      </div>

      <div style={{ display: markerType === "coord" ? "block" : "none" }}>
        <label style={{ fontSize: 13 }}>Latitude</label>
        <input
          ref={latInputRef}
          type="text"
          className="form-control mb-2"
          placeholder="Ex: -23.5468"
          defaultValue={inputValuesRef.current.lat}
          onChange={(e) => { inputValuesRef.current.lat = e.target.value; }}
          disabled={fieldsDisabled}
        />

        <label style={{ fontSize: 13 }}>Longitude</label>
        <input
          ref={lngInputRef}
          type="text"
          className="form-control mb-3"
          placeholder="Ex: -46.6353"
          defaultValue={inputValuesRef.current.lng}
          onChange={(e) => { inputValuesRef.current.lng = e.target.value; }}
          disabled={fieldsDisabled}
        />
      </div>

      <div style={{ display: "flex", gap: 10, marginTop: 20, flexDirection: "column" }}>
        {isViewMode && !isEditing && (
          <button
            className="btn btn-primary w-100"
            onClick={handleEditFromView}
          >
            Editar
          </button>
        )}

        {!isViewMode || isEditing ? (
          <>
            <div style={{ display: "flex", gap: 10 }}>

              <button
                className="btn btn-primary w-50"
                disabled={loading}
                onClick={async (e) => {
                  e.preventDefault();
                  e.stopPropagation();

                  const currentMarkerName = (nameInputRef.current?.value ?? "").trim();
                  const currentAddress = (addressInputRef.current?.value ?? "").trim();
                  const currentLatitude = (latInputRef.current?.value ?? "").trim();
                  const currentLongitude = (lngInputRef.current?.value ?? "").trim();

                  if (markerType === 'end' && !currentAddress) {
                    setToastParams(JSON.stringify({
                      show: true,
                      title: "Atenção",
                      body: "Informe um endereço para buscar.",
                      color: "Warning"
                    }));
                    return;
                  }

                  if (markerType === 'coord' && (!currentLatitude || !currentLongitude)) {
                    setToastParams(JSON.stringify({
                      show: true,
                      title: "Atenção",
                      body: "Informe latitude e longitude.",
                      color: "Warning"
                    }));
                    return;
                  }

                  if (!Number(radius) || Number(radius) <= 0) {
                    setToastParams(JSON.stringify({
                      show: true,
                      title: "Atenção",
                      body: "Informe um raio válido.",
                      color: "Warning"
                    }));
                    return;
                  }

                  await handleView(null, {
                    address: currentAddress,
                    latitude: currentLatitude,
                    longitude: currentLongitude,
                    markerName: currentMarkerName
                  });
                }}
              >
                {loading ? (
                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                ) : (
                  "Exibir"
                )}
              </button>

              <button
                className="btn btn-success w-50"
                disabled={savingPoint}
                onClick={async (e) => {
                  e.preventDefault();
                  e.stopPropagation();

                  const currentMarkerName = (nameInputRef.current?.value ?? "").trim();
                  const currentAddress = (addressInputRef.current?.value ?? "").trim();
                  const currentLatitude = (latInputRef.current?.value ?? "").trim();
                  const currentLongitude = (lngInputRef.current?.value ?? "").trim();

                  if (!currentMarkerName) {
                    setToastParams(JSON.stringify({
                      show: true,
                      title: "Atenção",
                      body: "Informe um nome para o marcador.",
                      color: "Warning"
                    }));
                    return;
                  }

                  if (markerType === 'end' && !currentAddress) {
                    setToastParams(JSON.stringify({
                      show: true,
                      title: "Atenção",
                      body: "Informe um endereço.",
                      color: "Warning"
                    }));
                    return;
                  }

                  if (markerType === 'coord' && (!currentLatitude || !currentLongitude)) {
                    setToastParams(JSON.stringify({
                      show: true,
                      title: "Atenção",
                      body: "Informe latitude e longitude.",
                      color: "Warning"
                    }));
                    return;
                  }

                  if (!Number(radius) || Number(radius) <= 0) {
                    setToastParams(JSON.stringify({
                      show: true,
                      title: "Atenção",
                      body: "Informe um raio válido.",
                      color: "Warning"
                    }));
                    return;
                  }

                  await handleInsert({
                    address: currentAddress,
                    latitude: currentLatitude,
                    longitude: currentLongitude,
                    markerName: currentMarkerName
                  });
                }}
              >
                {savingPoint ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                  </>
                ) : (
                  isEditing ? "Atualizar" : "Inserir"
                )}
              </button>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
};


export default function VisitPoints() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();
  const lat = location?.state?.lat;
  const lng = location?.state?.lng;

  const latInputRef = useRef(null)
  const lngInputRef = useRef(null)

  useEffect(() => {
    dispatch({ type: "title", value: "Pontos de Visita" });
    fetchCoordinates();
    setActiveTab("grid");
    setIsViewMode(false);
    setIsEditing(false);
    setEditingItem(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [markerType, setMarkerType] = useState('end');
  const [markerName, setMarkerName] = useState('');
  const [radius, setRadius] = useState(50);
  const [address, setAddress] = useState('');
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');

  const [coordinatesList, setCoordinatesList] = useState([]);
  const [selectedCoordinate, setSelectedCoordinate] = useState(null);
  const [pageSize, setPageSize] = useState(10);

  const [loading, setLoading] = useState(false);
  const [savingPoint, setSavingPoint] = useState(false);
  const [toastParams, setToastParams] = useState('');
  const [modalVisible, setModalVisible] = useState(false);
  const [addressOptions, setAddressOptions] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [showModalEdit, setShowModalEdit] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [addressToDelete, setAddressToDelete] = useState(null);
  const [activeTab, setActiveTab] = useState("grid");
  const [isViewMode, setIsViewMode] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const fetchCoordinates = async () => {
    try {
      const response = await controlService.findCoordinate(MARKER_TYPE_ID);
      if (Array.isArray(response)) setCoordinatesList(response);
    } catch (error) {
      console.error("Erro ao buscar coordenadas:", error);
    } finally {
      setLoading(false);
    }
  };

  const reverseGeocode = async (lat, lng) => {
    try {
      if (!window.google || !window.google.maps) return null;
      const geocoder = new window.google.maps.Geocoder();
      return await new Promise((resolve) => {
        geocoder.geocode({ location: { lat: Number(lat), lng: Number(lng) } }, (results, status) => {
          if (status === window.google.maps.GeocoderStatus.OK && results?.length) {
            resolve(results[0]?.formatted_address || null);
          } else {
            resolve(null);
          }
        });
      });
    } catch (e) {
      return null;
    }
  };

  const handleView = async (selectAddress, overrideValues = {}) => {
    if (activeTab !== "map") {
      setActiveTab("map");
    }
    setLoading(true);

    const currentAddress = overrideValues.address !== undefined ? overrideValues.address : address;
    const currentLatitude = overrideValues.latitude !== undefined ? overrideValues.latitude : latitude;
    const currentLongitude = overrideValues.longitude !== undefined ? overrideValues.longitude : longitude;
    const currentMarkerName = overrideValues.markerName !== undefined ? overrideValues.markerName : markerName;

    var payload = {
      markerType,
      markerName: currentMarkerName,
      radius: radius || 50,
      address: currentAddress || "",
      latitude: currentLatitude || "",
      longitude: currentLongitude || "",
    };

    if (markerType == "end") {
      const searchAddress = (currentAddress && currentAddress.trim()) || selectAddress;
      if (!searchAddress || searchAddress.trim() === "") {
        setToastParams(
          JSON.stringify({
            show: true,
            title: "Atenção",
            body: "Informe um endereço para buscar.",
            color: "Warning",
          })
        );
        setLoading(false);
        return;
      }

      try {
        const response = await controlService.findAddress(searchAddress);

        if (response && response.length > 0) {
          if (response.length === 1) {
            payload.latitude = Number(response[0].latitude);
            payload.longitude = Number(response[0].longitude);
            setSelectedCoordinate(payload);
          } else {
            setAddressOptions(response);
            setModalVisible(true);
          }
        } else {
          setToastParams(
            JSON.stringify({
              show: true,
              title: "Atenção",
              body: "Coordenada para este endereço não encontrada, favor informe um endereço válido.",
              color: "Warning",
            })
          );
        }
      } catch (error) {
        console.error("Erro na busca de coordenadas:", error);
        setToastParams(
          JSON.stringify({
            show: true,
            title: "Erro",
            body: "Erro ao buscar endereço. Verifique sua conexão e tente novamente.",
            color: "Danger",
          })
        );
      } finally {
        setLoading(false);
      }
    }

    if (markerType == "coord") {
      if (!currentLatitude || !currentLongitude) {
        setToastParams(
          JSON.stringify({ show: true, title: "Atenção", body: "Informe latitude e longitude.", color: "Warning" })
        );
        setLoading(false);
        return;
      }
      setSelectedCoordinate({
        markerType,
        markerName: currentMarkerName,
        radius: radius || 50,
        address: currentAddress,
        latitude: Number(currentLatitude),
        longitude: Number(currentLongitude),
      });
      setLoading(false);
    }
  };

  const handleAddressSelect = (address) => {
    const selected = addressOptions.find((opt) => opt.location ?? opt.address === address);
    if (selected) {
      const payload = {
        markerType,
        markerName,
        radius: radius || 50,
        address: selected.address ?? selected.location,
        latitude: Number(selected.latitude),
        longitude: Number(selected.longitude),
      };
      setSelectedCoordinate(payload);
      setModalVisible(false);
    }
  };

  const handleInsert = async (overrideValues = {}) => {
    const currentMarkerName = overrideValues.markerName !== undefined ? overrideValues.markerName : markerName;
    const currentAddress = overrideValues.address !== undefined ? overrideValues.address : address;
    const currentLatitude = overrideValues.latitude !== undefined ? overrideValues.latitude : latitude;
    const currentLongitude = overrideValues.longitude !== undefined ? overrideValues.longitude : longitude;

    if (!currentMarkerName) {
      setToastParams(JSON.stringify({ show: true, title: "Atenção", body: "Informe um nome para o marcador.", color: 'Warning' }));
      return;
    }

    if (markerType == "coord" && (!currentLongitude || !currentLatitude)) {
      setToastParams(JSON.stringify({ show: true, title: "Atenção", body: "Informe as coordenadas, latitude e longitude.", color: 'Warning' }));
      return;
    }

    if (markerType == "end" && !currentAddress) {
      setToastParams(JSON.stringify({ show: true, title: "Atenção", body: "Informe as endereço.", color: 'Warning' }));
      return;
    }

    try {
      setSavingPoint(true);

      let latNum = null;
      let lngNum = null;
      let addressStr = currentAddress || "";

      if (markerType === "coord") {
        latNum = Number(currentLatitude);
        lngNum = Number(currentLongitude);
      } else {
        if (!selectedCoordinate || !selectedCoordinate.latitude || !selectedCoordinate.longitude) {
          setToastParams(
            JSON.stringify({ show: true, title: "Atenção", body: "Clique em Exibir e selecione o endereço primeiro.", color: 'Warning' })
          );
          setSavingPoint(false);
          return;
        }
        latNum = Number(selectedCoordinate.latitude);
        lngNum = Number(selectedCoordinate.longitude);
        addressStr = selectedCoordinate.address || addressStr;
      }

      if (!addressStr && latNum != null && lngNum != null) {
        const resolved = await reverseGeocode(latNum, lngNum);
        if (resolved) {
          addressStr = resolved;
        } else {
          setToastParams(
            JSON.stringify({ show: true, title: "Atenção", body: "Endereço não informado e não foi possível obter pelo mapa.", color: 'Warning' })
          );
          setSavingPoint(false);
          return;
        }
      }

      const payload = {
        markerTypeId: MARKER_TYPE_ID,
        markerDescription: currentMarkerName,
        latitude: latNum,
        longitude: lngNum,
        address: addressStr,
        radius: Number(radius) || 50,
        area: "NA",
      };

      if (isEditing && editingItem) {
        payload.markerId = editingItem.markerId;
        payload.markerTypeId = editingItem.markerTypeId;

        const response = await controlService.updateCoordinate(payload);

        if (response && !response.error) {
          setToastParams(JSON.stringify({ show: true, title: "Sucesso", body: "Marcador atualizado.", color: 'success' }));
          setSelectedCoordinate(null);
          setIsEditing(false);
          setIsViewMode(false);
          setEditingItem(null);
          await fetchCoordinates();
          setActiveTab("grid");
        } else {
          const body = response?.message || response?.friendlyMessage || "Não foi possível atualizar o marcador.";
          setToastParams(JSON.stringify({ show: true, title: "Erro", body, color: 'Danger' }));
        }
      } else {
        const response = await visitsService.createMarker(payload);

        if (response && !response.error) {
          setToastParams(JSON.stringify({ show: true, title: "Sucesso", body: "Marcador criado.", color: 'success' }));
          setSelectedCoordinate(null);
          setIsEditing(false);
          setIsViewMode(false);
          setEditingItem(null);
          await fetchCoordinates();
          setActiveTab("grid");
        } else {
          const body = response?.message || response?.friendlyMessage || "Não foi possível criar o marcador.";
          setToastParams(JSON.stringify({ show: true, title: "Erro", body, color: 'Danger' }));
        }
      }

    } catch (error) {
      console.error("Erro ao salvar/criar marcador:", error);
      setToastParams(
        JSON.stringify({
          show: true,
          title: "Erro",
          body: error?.message || "Erro ao processar a operação. Tente novamente.",
          color: "Danger",
        })
      );
    } finally {
      setSavingPoint(false);
    }

  };

  const handleEdit = (row) => {
    const coordinatePayload = {
      markerType: "coord",
      markerName: row.markerDescription,
      radius: Number(row.radius) || 50,
      address: row.address || row.location || "",
      latitude: Number(row.latitude),
      longitude: Number(row.longitude),
    };

    setMarkerName(row.markerDescription || "");
    setRadius(Number(row.radius) || 50);
    setAddress(row.address || row.location || "");
    setLatitude(String(row.latitude));
    setLongitude(String(row.longitude));
    setMarkerType("coord");

    setSelectedCoordinate(coordinatePayload);

    setIsViewMode(false);
    setIsEditing(true);
    setEditingItem(row);

    setActiveTab("map");
  };

  const handleEditFromView = useCallback(() => {
    setIsViewMode(false);
    setIsEditing(true);
  }, []);

  const openDeleteModal = (address) => {
    setAddressToDelete(address);
    setShowConfirmModal(true);
  };

  const confirmDelete = async () => {
    try {
      const response = await controlService.deleteAddress(
        addressToDelete.markerId,
        addressToDelete.markerTypeId
      );

      if (response?.data?.hasLinkedVehicles || response?.status === 409) {
        setToastParams(
          JSON.stringify({
            show: true,
            title: "Não permitido",
            body: "Existem veículos vinculados a este ponto. Desvincule para continuar.",
            color: "Warning",
          })
        );
        setShowConfirmModal(false);
        return;
      }

      if (response) {
        setToastParams(
          JSON.stringify({
            show: true,
            title: "Sucesso",
            body: "Endereço excluído com sucesso.",
            color: "Success",
          })
        );
        fetchCoordinates();
      } else {
        setToastParams(
          JSON.stringify({
            show: true,
            title: "Erro",
            body: "Não foi possível excluir o endereço.",
            color: "Danger",
          })
        );
      }
    } catch (error) {
      console.error("Erro ao excluir o endereço:", error);

      if (error?.response?.status === 409) {
        setToastParams(
          JSON.stringify({
            show: true,
            title: "Não permitido",
            body: "Este ponto possui veículos vinculados e não pode ser excluído.",
            color: "Warning",
          })
        );
      } else {
        setToastParams(
          JSON.stringify({
            show: true,
            title: "Erro",
            body: "Falha inesperada ao excluir.",
            color: "Danger",
          })
        );
      }
    } finally {
      setShowConfirmModal(false);
      setAddressToDelete(null);
    }
  };

  const handleSave = async () => {
    if (!selectedAddress) {
      setToastParams(
        JSON.stringify({
          show: true,
          title: "Atenção",
          body: "Nenhum endereço selecionado para atualizar.",
          color: "Warning",
        })
      );
      return;
    }

    setSavingPoint(true);
    try {
      const payload = {
        markerId: selectedAddress.markerId,
        markerTypeId: selectedAddress.markerTypeId,
        markerDescription: selectedAddress.markerDescription,
        latitude: Number(selectedAddress.latitude),
        longitude: Number(selectedAddress.longitude),
        address: selectedAddress.address,
        radius: Number(selectedAddress.radius) || 50,
        area: selectedAddress.area || "NA",
      };
      const response = await controlService.updateCoordinate(payload);
      if (response && !response.error) {
        setToastParams(
          JSON.stringify({
            show: true,
            title: "Sucesso",
            body: "Endereço atualizado com sucesso.",
            color: "Success",
          })
        );
        setShowModalEdit(false);
        setSelectedCoordinate(null);
        await fetchCoordinates();
        setActiveTab("grid");
      } else {
        const body = response?.message || response?.friendlyMessage || "Não foi possível atualizar o endereço.";
        setToastParams(JSON.stringify({ show: true, title: "Erro", body, color: "Danger" }));
      }
    } catch (error) {
      console.error("Erro ao salvar as alterações:", error);
      setToastParams(
        JSON.stringify({
          show: true,
          title: "Erro",
          body: error?.message || "Erro ao atualizar o endereço. Tente novamente.",
          color: "Danger",
        })
      );
    } finally {
      setSavingPoint(false);
    }
  };

  useEffect(() => {
    if (lng && lat) {
      setLatitude(lat);
      setLongitude(lng);
      setMarkerType('coord');

      var coordinate = {
        latitude: lat,
        longitude: lng
      }

      setSelectedCoordinate(coordinate);
    }
    // eslint-disable-next-line
  }, [lng, lat]);

  const calculatePageSize = () => {
    const windowHeight = window.innerHeight;

    let headerHeight = 60;
    let cardPadding = 30;
    let titleHeight = 50;
    let gridHeaderHeight = 35;
    let paginationHeight = 30;
    let rowHeight = 38;

    const totalFixedHeight =
      headerHeight +
      cardPadding +
      titleHeight +
      gridHeaderHeight +
      paginationHeight;

    const availableHeight = windowHeight - totalFixedHeight;

    const calculatedPageSize = Math.max(
      Math.floor(availableHeight / rowHeight),
      10
    );

    setPageSize(calculatedPageSize);
  };

  useLayoutEffect(() => {
    calculatePageSize();
  }, []);

  useEffect(() => {
    let timeoutId;
    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        calculatePageSize();
      }, 300);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      clearTimeout(timeoutId);
    };
  }, []);

  const handleViewOnMap = (row) => {
    const coordinatePayload = {
      markerType: "coord",
      markerName: row.markerDescription,
      radius: Number(row.radius) || 50,
      address: row.address || row.location || "",
      latitude: Number(row.latitude),
      longitude: Number(row.longitude),
    };

    setMarkerName(row.markerDescription || "");
    setRadius(Number(row.radius) || 50);
    setAddress(row.address || row.location || "");
    setLatitude(String(row.latitude));
    setLongitude(String(row.longitude));
    setMarkerType("coord");

    setSelectedCoordinate(coordinatePayload);

    setIsViewMode(true);
    setIsEditing(false);
    setEditingItem(row);

    setActiveTab("map");
  };

  return (
    <>
      <style>{`
        .modal-backdrop-high-z {
          z-index: 99998 !important;
        }
        .modal-high-z {
          z-index: 99999 !important;
        }
      `}</style>

      <Modal
        show={modalVisible}
        onHide={() => setModalVisible(false)}
        centered
        backdropClassName="modal-backdrop-high-z"
        dialogClassName="modal-high-z"
      >
        <Modal.Header closeButton>
          <Modal.Title>Selecione um endereço</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ul className="list-group">
            {addressOptions.map((opt, index) => (
              <li key={index} className="list-group-item" style={{ marginBottom: 20, borderTop: "1px solid #fff" }}>
                <Button
                  variant="link"
                  style={{ color: "var(--theme-primary)" }}
                  onClick={() => handleAddressSelect(opt.location ?? opt.address)}
                >
                  {opt.location ?? opt.address}
                </Button>
              </li>
            ))}
          </ul>
        </Modal.Body>
      </Modal>

      <Modal show={showModalEdit} onHide={() => setShowModalEdit(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Editar Coordenada</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loading && <p>Processando...</p>}
          {selectedAddress && (
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Nome:</Form.Label>
                <Form.Control
                  type="text"
                  value={selectedAddress.markerDescription}
                  onChange={(e) =>
                    setSelectedAddress({ ...selectedAddress, markerDescription: e.target.value })
                  }
                  disabled={loading}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Raio (m):</Form.Label>
                <Form.Select
                  className="form-control"
                  value={Number(selectedAddress.radius) || 50}
                  onChange={(e) => setSelectedAddress({ ...selectedAddress, radius: Number(e.target.value) })}
                  disabled={loading}
                >
                  <option value="0">--Raio --</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                  <option value="200">200</option>
                  <option value="500">500</option>
                  <option value="1000">1000</option>
                </Form.Select>
              </Form.Group>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModalEdit(false)} disabled={savingPoint}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={savingPoint}>
            {savingPoint ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Salvando...
              </>
            ) : (
              t("BUTTONS.SAVE")
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirmar exclusão</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {addressToDelete && (
            <p>
              Deseja realmente excluir o endereço{" "}
              <strong>{addressToDelete.address}</strong>?
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>
            Cancelar
          </Button>
          <Button variant="danger" onClick={confirmDelete}>
            Excluir
          </Button>
        </Modal.Footer>
      </Modal>

      <div style={{
        display: 'flex',
        justifyContent: 'center',
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        borderRadius: '12px',
        padding: '4px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        maxWidth: '350px',
        margin: '0 auto 20px auto'
      }}>
        <button
          onClick={() => {
            setActiveTab("grid")
            setEditingItem(null)
            setIsEditing(false)
          }
          }
          style={{
            padding: '8px 20px',
            border: 'none',
            borderRadius: '8px',
            backgroundColor: activeTab === "grid" ? 'var(--theme-primary)' : 'transparent',
            color: activeTab === "grid" ? 'white' : '#6c757d',
            fontWeight: activeTab === "grid" ? '600' : '500',
            fontSize: '13px',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
            boxShadow: activeTab === "grid" ? '0 2px 8px rgba(108, 92, 231, 0.3)' : 'none',
            minWidth: '220px'
          }}
        >
          <FaCarAlt size={14} />
          Ponto de visitas
        </button>

        <button
          onClick={() => {
            setIsViewMode(false);
            setIsEditing(false);
            setEditingItem(null);
            setSelectedCoordinate(null);
            if (!markerName && !address && !latitude && !longitude) {
              setMarkerType("end");
              setRadius(50);
            }
            setActiveTab("map");
          }}
          style={{
            padding: '8px 20px',
            border: 'none',
            borderRadius: '8px',
            backgroundColor: activeTab === "map" ? 'var(--theme-primary)' : 'transparent',
            color: activeTab === "map" ? 'white' : '#6c757d',
            fontWeight: activeTab === "map" ? '600' : '500',
            fontSize: '13px',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
            boxShadow: activeTab === "map" ? '0 2px 8px rgba(108, 92, 231, 0.3)' : 'none',
            minWidth: '120px'
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
          </svg>
          Mapa
        </button>
      </div>

      <div className='container-xxl flex-grow-1 container-p-y'>
        <ToastComponent params={toastParams} closeToast={() => setToastParams(JSON.stringify({ show: false }))} />

        {activeTab === "map" && (
          <>
            <VisitSidebar
              key="visit-sidebar"
              markerType={markerType}
              setMarkerType={setMarkerType}
              markerName={markerName}
              setMarkerName={setMarkerName}
              radius={radius}
              setRadius={setRadius}
              address={address}
              setAddress={setAddress}
              latitude={latitude}
              setLatitude={setLatitude}
              longitude={longitude}
              setLongitude={setLongitude}
              handleView={handleView}
              handleInsert={handleInsert}
              isViewMode={isViewMode}
              isEditing={isEditing}
              handleEditFromView={handleEditFromView}
              loading={loading}
              savingPoint={savingPoint}
              setToastParams={setToastParams}
            />

            <RadiusCoordinatedMap
              coordinates={selectedCoordinate}
              mapLoading={loading}
              circleColor="#2563eb"
              isEditing={isEditing}
              setLatitude={setLatitude}
              setLongitude={setLongitude}
              latInputRef={latInputRef}
              lngInputRef={lngInputRef}                    
            />

          </>
        )}

        {activeTab === "grid" && (
          <GridComponent
            dataSource={coordinatesList}
            allowPaging={true}
            pageSettings={{ pageSize }}
          >
            <ColumnsDirective>
              <ColumnDirective
                headerText="Ponto de visita"
                width="25%"
                textAlign="Left"
                template={(data) => (
                  <span>{data.markerDescription}</span>
                )}
              />
              <ColumnDirective
                headerText="Endereço"
                width="30%"
                textAlign="Left"
                template={(props) => {
                  return (
                    <a
                      href={`https://maps.google.com/?q=${getLocationCoordinates(
                        "location",
                        props
                      )}`}
                      target="_blank"
                      rel="noreferrer"
                      style={{
                        cursor: "pointer",
                        textDecoration: "underline",
                        color: "inherit",
                        fontSize: "13px",
                      }}
                    >
                      <div>{props.address ?? props.location}</div>
                    </a>
                  );
                }}
              />

              <ColumnDirective
                headerText="Raio"
                field="radius"
                width="15%"
                textAlign="Left"
              />

              <ColumnDirective
                headerText="Mapa"
                width="10%"
                textAlign="Center"
                template={(row) => (
                  <button
                    className="btn btn-link"
                    style={{ color: "#4285F4" }}
                    onClick={() =>
                      handleViewOnMap(row)
                    }
                  >
                    <FaMapMarkerAlt color="red" size={20} />
                  </button>
                )}
              />

              <ColumnDirective
                headerText="Editar"
                width="10%"
                textAlign="Center"
                template={(row) => (
                  <button
                    className="btn btn-link"
                    style={{ color: "var(--text-primary)" }}
                    onClick={() => handleEdit(row)}
                  >
                    <FaEdit size={18} />
                  </button>
                )}
              />

              <ColumnDirective
                headerText={t("BUTTONS.DELETE")}
                width="10%"
                textAlign="Center"
                template={(row) => (
                  <button
                    className="btn btn-link"
                    style={{ color: "var(--text-primary)" }}
                    onClick={() => openDeleteModal(row)}
                  >
                    <FaTrash size={18} />
                  </button>
                )}
              />
            </ColumnsDirective>

            <Inject services={[Page]} />
          </GridComponent>
        )}
      </div>
    </>
  );
}